/**
 * Route guard for ``is_platform_admin``-only pages.
 *
 * Distinct from ``RoleGate`` which checks the customer-facing
 * SUPER_ADMIN/HR_ADMIN/MANAGER/EMPLOYEE roles. Platform admin is
 * the operator-of-the-SaaS bit — only Lenavio staff have it.
 *
 * Behavior:
 *   - User missing or not loaded yet → render nothing (the
 *     ProtectedRoute upstream handles the unauth case).
 *   - User present but not platform-admin → redirect to ``/``.
 *   - User is platform-admin → render the wrapped component.
 *
 * Why a redirect instead of an inline "access denied" message:
 * we don't want to hint at the existence of `/admin/*` URLs to
 * customer-side users browsing the app.
 */
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

interface RequirePlatformAdminProps {
  children: React.ReactNode;
}

export function RequirePlatformAdmin({ children }: RequirePlatformAdminProps) {
  const { user } = useAuth();
  if (!user) return null; // ProtectedRoute already redirected to /login
  if (!user.is_platform_admin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export default RequirePlatformAdmin;
